import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { DesignStudioForwardSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";

const colourStyles = {
  control: (styles) => ({
    ...styles,
    border: "1px solid #bfc9d4",
    boxShadow: "#000 !important",
    borderRadius: "6px",
    padding: "4px",
    "&:hover": {
      border: "1px solid #bfc9d4",
    },
  }),
  multiValue: (styles) => {
    return {
      ...styles,
      backgroundColor: "#606060",
    };
  },
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#fff",
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: "#000",
    "& > svg": {
      backgroundColor: "#fff",
      borderRadius: "10px",
      marginLeft: "2px",
      marginRight: "2px",
    },
    ":hover": {
      backgroundColor: data.color,
      color: data.color,
    },
  }),
  option: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: "white",
      color: "#000",
      ":hover": {
        backgroundColor: "#f5f5f5",
      },
    };
  },
};

const user = localStorage.getItem("user");
const userData = JSON.parse(user);
const userType = userData?.user_type;

const DesignStudioEdit = () => {
  const [orderData, setOrderData] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const location = useLocation();
  const [files, setFiles] = useState([]); // raw uploaded files (for Formik)
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_URL}/get-orders-list`)
      .then((res) => {
        setOrderData(
          res.data.data.map((item) => ({
            label: item.id,
            value: item.id,
          }))
        );
      })
      .catch((err) => {
        if (err.response.data.message === "Unauthorized!") {
          Navigate("/admin/login");
        }
      });
  }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const previews = selectedFiles.map((file) => ({
      url: URL.createObjectURL(file),
      type: file.type.startsWith("image") ? "image" : "video",
      name: file.name,
      file,
    }));

    const updatedFiles = [...files, ...selectedFiles];
    const updatedPreviews = [...mediaPreviews, ...previews];

    setFiles(updatedFiles);
    setMediaPreviews(updatedPreviews);
    AddDesignStudios.setFieldValue("image", updatedFiles);
  };

  const handleThumbnailClick = (index) => {
    setActivePreviewIndex(index);
  };

  const fetchDesignStudioDetails = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/design-studio/get-all`,
        {
          params: {
            order_id: location?.state?.id,
          },
          headers: {
            "j-authorization": localStorage.getItem("Token"),
          },
        }
      );

      if (res.status === 200 && res.data?.image) {
        const images = res.data.image;

        setMediaPreviews(
          images.map((img) => `${process.env.REACT_APP_URL}/uploads/${img}`)
        );
      }
    } catch (error) {
      console.error("Failed to fetch design studio details", error);
    }
  };

  // Load once on component mount
  useEffect(() => {
    fetchDesignStudioDetails();
  }, []);

  const handleRemoveMedia = async (indexToRemove) => {
    const fileToDelete = files[indexToRemove];

    try {
      const orderId = AddDesignStudios.values.order_id;

      if (!orderId) {
        toast.error("Order ID is required before removing media.");
        return;
      }

      const config = {
        headers: {
          "j-authorization": localStorage.getItem("Token"),
        },
      };

      const res = await axios.delete(
        `${process.env.REACT_APP_URL}/admin/design-studio/delete-media`,
        {
          data: {
            order_id: location?.state?.id,
            image: fileToDelete, // ✅ FIXED: send only filename
          },
          ...config,
        }
      );

      if (res.status === 200) {
        toast.success(res.data.message);

        const updatedPreviews = mediaPreviews.filter(
          (_, i) => i !== indexToRemove
        );
        const updatedFiles = files.filter((_, i) => i !== indexToRemove);

        setMediaPreviews(updatedPreviews);
        setFiles(updatedFiles);
        AddDesignStudios.setFieldValue("image", updatedFiles);
        fetchDesignStudioDetails(); // 👈 Add this
      } else {
        toast.error("Failed to remove media.");
      }
    } catch (err) {
      console.error("Remove media error:", err);
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  const Navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    order_id: "",
    image: null,
  });

  // useEffect(() => {
  //   if (location?.state?.id) {
  //     setInitialValues((prev) => ({
  //       ...prev,
  //       order_id: parseInt(location?.state?.id),
  //       image: location.state.images,
  //     }));
  //   }
  // }, [location]);

  // useEffect(() => {
  //   if (initialValues.image?.length) {
  //     const previews = initialValues.image.map((img) => {
  //       const fullUrl = `${process.env.REACT_APP_IMAGE_URL}/uploads/${img}`;

  //       const isImage = img.match(/\.(jpeg|jpg|png|gif|webp)$/i);
  //       return {
  //         url: fullUrl, // ✅ Full image/video URL
  //         type: isImage ? "image" : "video",
  //       };
  //     });

  //     setMediaPreviews(previews);
  //   }
  // }, [initialValues.image]);

  useEffect(() => {
    if (location?.state?.id) {
      const images = location?.state?.images || [];

      setInitialValues((prev) => ({
        ...prev,
        order_id: parseInt(location.state.id),
        image: images, // for Formik
      }));

      // 👇 Update both files and mediaPreviews
      const mediaFiles = images.map((img) => {
        const isImage = img.match(/\.(jpeg|jpg|png|gif|webp)$/i);
        return {
          url: `${process.env.REACT_APP_IMAGE_URL}/uploads/${img}`,
          type: isImage ? "image" : "video",
          name: img,
        };
      });

      setMediaPreviews(mediaFiles);
      setFiles(images); // files = ["file1.jpg", "video1.mp4", ...]
    }
  }, [location]);

  const AddDesignStudios = useFormik({
    initialValues,
    enableReinitialize: true, // <-- Yeh line add karein!
    validationSchema: DesignStudioForwardSchema,

    onSubmit: async (values, action) => {
      const user = localStorage.getItem("user");
      const userData = JSON.parse(user);
      const designation = userData?.designation;
      const designer_id = userData?.id;

      const formData = new FormData();

      // Common for both
      formData.append("order_id", values.order_id);

      // Change key name based on designation
      if (designation === "Graphic Designer") {
        formData.append("designer_id", designer_id);
        values.image.forEach((file) => {
          formData.append("design_url", file); // 👈 key for graphic designer
        });
      } else {
        values.image.forEach((file) => {
          formData.append("image", file); // 👈 key for others
        });
      }

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      };

      try {
        const endpoint =
          designation === "Graphic Designer"
            ? "/admin/design-studio/add-graphic-designer"
            : "/admin/design-studio/add-forward";

        const res = await axios.post(
          `${process.env.REACT_APP_URL}${endpoint}`, // ✅ dynamically switch API URL
          formData,
          config
        );

        if (res.status === 200) {
          toast.success(res?.data?.message);
          Navigate("/admin/design-studio", {
            state: res.data.msg,
          });
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Something went wrong");
      }
    },
  });

  //   const UserRole = () => {
  //     axios
  //       .get(`${process.env.REACT_APP_URL}/common/roles`)
  //       .then((res) => {
  //         setUserRole(res.data.roles);
  //       })
  //       .catch((err) => {
  //         if (err.response.data.message === "Unauthorized!") {
  //           Navigate("/admin/login");
  //         }
  //       });
  //   };

  useEffect(() => {
    // UserRole();
  }, []);

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />
      <div className="al-head">
        <div>
          <h1 className="al-title">Edit Design Studio</h1>
          <p className="al-sub">Update this design studio entry.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

        <div className="al-card" style={{ padding: "20px" }}>
          <div>
            <div className="row">
              <div className="col-lg-6 col-12">
                <form onSubmit={AddDesignStudios.handleSubmit}>
                  <div className="form-group">
                    <label className="al-label">
                      Order ID
                    </label>

                    <ReactSelect
                      // isMulti
                      options={orderData}
                      isClearable={false}
                      styles={colourStyles}
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Order ID"
                      onBlur={AddDesignStudios.handleBlur}
                      name="order_id"
                      onChange={(e) =>
                        AddDesignStudios?.setFieldValue("order_id", e?.value)
                      }
                      value={
                        orderData.find(
                          (item) =>
                            item.value === AddDesignStudios.values.order_id
                        ) || null
                      }
                    />

                    {AddDesignStudios.errors.order_id &&
                    AddDesignStudios.touched.order_id ? (
                      <h6 className="text-danger mt-2 ml-1">
                        {AddDesignStudios.errors.order_id}
                      </h6>
                    ) : null}
                  </div>

                  <div className="form-group">
                    <label className="al-label">
                      Image
                    </label>
                    <label
                      className="custom-file-container__custom-file mt-0"
                      style={{
                        width: "100%",
                        height: "310px",
                        border: "1px dashed #ccc",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f9f9f9",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="file"
                        name="image"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleFileChange}
                        onBlur={AddDesignStudios.handleBlur}
                        style={{ display: "none" }}
                      />

                      <span
                        style={{
                          fontSize: "16px",
                          color: "#aaa",
                          marginBottom: "10px",
                        }}
                      >
                        Drag an image here
                      </span>

                      <button
                        type="button"
                        style={{
                          marginTop: "10px",
                          padding: "8px 16px",
                          backgroundColor: "#007bff",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          document.querySelector('input[name="image"]').click()
                        }
                      >
                        Choose an image/video to upload
                      </button>
                    </label>

                    {/* Show previews here */}
                  </div>
                  {AddDesignStudios.errors.image &&
                  AddDesignStudios.touched.image ? (
                    <h6 className="text-danger mt-2 ml-1">
                      {AddDesignStudios.errors.image}
                    </h6>
                  ) : null}

                  <div className="al-actionbar" style={{ marginTop: 16 }}>
                    <button type="button" className="al-btn" onClick={() => Navigate(-1)}>Cancel</button>
                    <button type="submit" className="al-btn al-btn--primary">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>

              <div
                className="col-lg-6 col-12"
                style={{
                  marginTop: "15px",
                  display: "flex",
                  gap: "5px",
                  flexDirection: "column",
                  flexWrap: "wrap",
                }}
              >
                {/* Main Preview Area */}
                {mediaPreviews.length > 0 && (
                  <div
                    style={{
                      width: "100%",
                      height: "400px",
                      border: "1px dashed #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#f9f9f9",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {/* Remove Icon for Main Preview */}
                    <span
                      onClick={() => handleRemoveMedia(activePreviewIndex)}
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "25px",
                        height: "25px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        zIndex: 2,
                        fontSize: "18px",
                        userSelect: "none",
                      }}
                    >
                      ×
                    </span>

                    {/* Media Display */}
                    {mediaPreviews[activePreviewIndex]?.type === "image" ? (
                      <img
                        src={mediaPreviews[activePreviewIndex]?.url}
                        alt="Main Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: "4px",
                        }}
                      />
                    ) : (
                      <video
                        controls
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: "4px",
                        }}
                      >
                        <source
                          src={mediaPreviews[activePreviewIndex]?.url}
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                )}

                {/* Thumbnail List */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                    marginTop: "15px",
                  }}
                >
                  {mediaPreviews.map((media, index) => (
                    <div
                      key={index}
                      onClick={() => setActivePreviewIndex(index)}
                      style={{
                        position: "relative",
                        width: "90px",
                        height: "90px",
                        borderRadius: "8px",
                        border:
                          index === activePreviewIndex
                            ? "2px solid #007bff"
                            : "1px solid #ccc",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                    >
                      {/* Remove icon */}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveMedia(index);
                        }}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: "pointer",
                          zIndex: 2,
                          fontSize: "14px",
                          userSelect: "none",
                        }}
                      >
                        ×
                      </span>

                      {/* Thumbnail Preview */}
                      {media.type === "image" ? (
                        <img
                          src={media.url}
                          alt={`preview-${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <video
                          muted
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        >
                          <source src={media.url} type="video/mp4" />
                        </video>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default DesignStudioEdit;
